import React, { useContext, useEffect } from 'react';
import { Icons } from '@nextiva/next-ui-components';
import {
  UserContext,
  actionTypes,
} from 'containers/pages/ManageUsers/usersContext';
import {
  useGetCXWorkspacesAtAccountLevel,
  useGetCXWorkspacesAtUserLevel,
} from 'hooks/useGetCXWorkspaces/useGetCXWorkspaces';
import {
  StyledContainer,
  StyledLabelContainer,
  StyledLabel,
  StyledHelpIcon,
  StyledSelect,
  StyledTooltip,
  StyledChipsContainer,
  StyledChip,
  StyledMenuItem,
} from './WorkspaceChipSelect.styles';

const TOOLTIP_TEXT =
  'A workspace is where you organize content & channels by team, function or role in Nextiva CX. All users must have at least 1 workspace assigned.';

interface WorkspaceChipSelectProps {
  flowType: string;
  userUuid: string;
  didUserInitiallyHaveCXLicense: boolean;
}

export const WorkspaceChipSelect = ({
  flowType,
  userUuid,
  didUserInitiallyHaveCXLicense,
}: WorkspaceChipSelectProps) => {
  const isEditUserFlowActive =
    flowType === 'EDIT_USER' && didUserInitiallyHaveCXLicense;

  useGetCXWorkspacesAtAccountLevel();
  useGetCXWorkspacesAtUserLevel(userUuid, didUserInitiallyHaveCXLicense);

  const {
    userState: {
      accountLevelCXWorkspaces,
      updatedUserLevelCXWorkspaces,
      newCXUserWorkspaces,
    },
    dispatch,
  } = useContext(UserContext);

  const workspacesToRender = isEditUserFlowActive
    ? updatedUserLevelCXWorkspaces
    : newCXUserWorkspaces;

  const updateWorkspaceState = (payload) => {
    if (isEditUserFlowActive) {
      dispatch({
        type: actionTypes.POPULATE_UPDATED_USER_LEVEL_CX_WORKSPACES,
        payload,
      });
    } else {
      dispatch({
        type: actionTypes.POPULATE_NEW_CX_USER_WORKSPACES,
        payload,
      });
    }
  };

  const onChipDelete = (event, workspaceToDelete) => {
    event.preventDefault();

    const updatedList = [...workspacesToRender];
    const filteredList = updatedList.filter((workspace) => {
      if (workspace?.id === workspaceToDelete?.id) return false;
      return true;
    });
    updateWorkspaceState(filteredList);
  };

  const onChipMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const onSelectChange = (event) => {
    const selectedWorkspace = event.target.value;

    const isWorkspaceAlreadySelected = workspacesToRender?.some(
      (workspace) => workspace?.id === selectedWorkspace?.id,
    );

    if (!isWorkspaceAlreadySelected) {
      const updatedList = [selectedWorkspace, ...workspacesToRender];
      updateWorkspaceState(updatedList);
    }
  };

  useEffect(() => {
    updateWorkspaceState([]);
  }, []);

  return (
    <StyledContainer>
      <StyledLabelContainer>
        <StyledLabel id="Field-label">WORKSPACE</StyledLabel>
        <StyledTooltip placement="top" title={TOOLTIP_TEXT}>
          <StyledHelpIcon
            icon={Icons.helpSolid}
            aria-label="Help"
            data-testid="PlatformAdminUI-WorkspaceSelect-HelpIcon"
          />
        </StyledTooltip>
      </StyledLabelContainer>
      <StyledSelect
        fullWidth
        value={{}}
        onChange={onSelectChange}
        MenuProps={{ disablePortal: true }}
        renderValue={() => (
          <StyledChipsContainer>
            {workspacesToRender?.map((workspace) => (
              <StyledChip
                enableWarningColor={workspacesToRender.length === 1}
                key={workspace?.id}
                label={workspace?.profileName}
                onMouseDown={(event) => onChipMouseDown(event)}
                onDelete={(event) => onChipDelete(event, workspace)}
                data-testid="PlatformAdminUI-WorkspaceSelect-Chip"
              />
            ))}
          </StyledChipsContainer>
        )}
        data-testid="PlatformAdminUI-WorkspaceSelect"
      >
        {accountLevelCXWorkspaces?.map((accountLevelCXWorkspace) => (
          <StyledMenuItem
            key={accountLevelCXWorkspace?.id}
            value={accountLevelCXWorkspace}
            data-testid="PlatformAdminUI-WorkspaceSelect-MenuItem"
          >
            {accountLevelCXWorkspace?.profileName}
          </StyledMenuItem>
        ))}
      </StyledSelect>
    </StyledContainer>
  );
};
